import type { HTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';
import { Skeleton } from '../../ui/skeleton';

export interface StepSkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Quantidade de campos simulados. */
  fields?: number;
  label?: string;
  /** Cabeçalho (logo + título + progresso). `false` quando o layout já o desenha. */
  header?: boolean;
  /** Botões do rodapé. `false` quando o layout já os desenha. */
  actions?: boolean;
}

/**
 * Esqueleto de uma etapa de formulário (cabeçalho com logo + título, barra de
 * progresso, campos e botões) — distinto de estado vazio e de erro.
 */
function StepSkeleton({
  fields = 4,
  label = 'Carregando',
  header = true,
  actions = true,
  className,
  ...props
}: StepSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      data-slot="step-skeleton"
      className={cn('flex w-full flex-col gap-6', className)}
      {...props}
    >
      {header ? (
        <div data-slot="step-skeleton-header" className="flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <Skeleton className="size-8" />
            <Skeleton className="h-9 w-2/3" />
          </div>
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-2 w-full" />
        </div>
      ) : null}
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} data-slot="step-skeleton-field" className="flex flex-col gap-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-11 w-full" />
        </div>
      ))}
      {actions ? (
        <div data-slot="step-skeleton-actions" className="flex justify-end gap-3">
          <Skeleton className="h-11 w-24" />
        </div>
      ) : null}
    </div>
  );
}
StepSkeleton.displayName = 'StepSkeleton';

export { StepSkeleton };
